import { css } from "../../../../styled-system/css";
import { HStack } from "../../../../styled-system/jsx";
import { formatMetricValue, type FormatOptions } from "../../../utils/formatNumber";
import type { CompareHighlight } from "../../../modules/merchandising/utils/metrics";
import { FaCaretDown, FaCaretUp } from "react-icons/fa6";
import Skeleton from "react-loading-skeleton";

interface MetricChangeRowProps {
  delta?: number | null;
  percent?: number | null;
  highlight: CompareHighlight;
  deltaFormat: FormatOptions;
  marketPlace: string;
  loading?: boolean;
}

export function MetricChangeRow({
  delta,
  percent,
  highlight,
  deltaFormat,
  marketPlace,
  loading = false,
}: MetricChangeRowProps) {
  const percentFormat: FormatOptions = {
    format: "percent",
    compact: true,
    decimalPlaces: 1,
    addPlus: true,
    marketPlace,
  };

  const displayDelta = formatMetricValue(delta, deltaFormat);
  const displayPercent = formatMetricValue(percent, percentFormat);

  const hasDelta = displayDelta !== "-";
  const hasPercent = displayPercent !== "-";

  if (!loading && !hasDelta && !hasPercent) return null;

  const value = delta ?? percent ?? 0;
  const isIncrease = value > 0;
  const isDecrease = value < 0;

  const positive = (highlight === "positiveIsGood" && isIncrease) || (highlight === "negativeIsGood" && isDecrease);
  const negative = (highlight === "positiveIsGood" && isDecrease) || (highlight === "negativeIsGood" && isIncrease);

  const tint =
    positive ? "trellisPalette.12"
    : negative ? "trellisPalette.17"
    : "trellisPalette.2";
  const pillBackground =
    positive ? "trellisPalette.12/15"
    : negative ? "trellisPalette.17/15"
    : "trellisPalette.2/20";

  if (loading) {
    return (
      <HStack gap={"small"} justifyContent={"end"} alignItems={"center"} width={"100%"}>
        <Skeleton borderRadius={"9999px"} width={"50px"} height={"20px"} />
        <Skeleton width={"40px"} height={"16px"} />
      </HStack>
    );
  }

  return (
    <HStack gap={"small"} justifyContent={"end"} alignItems={"center"} width={"100%"}>
      {hasDelta && (
        <span
          className={css({
            display: "inline-flex",
            gap: "xsmall",
            alignItems: "center",
            borderRadius: "pill",
            paddingY: "2px",
            paddingX: "small",
            color: tint,
            fontSize: "small",
            fontWeight: "bold",
            textTransform: "none",
            backgroundColor: pillBackground,
            whiteSpace: "nowrap",
          })}
        >
          {isIncrease && <FaCaretUp size={12} />}
          {isDecrease && <FaCaretDown size={12} />}
          {!(isIncrease || isDecrease) && null}
          <span>{displayDelta}</span>
        </span>
      )}
      {hasPercent && (
        <span
          className={css({
            color: tint,
            fontSize: "small",
            fontWeight: "bold",
          })}
        >
          {displayPercent}
        </span>
      )}
    </HStack>
  );
}
